import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Menu,
  Stack,
  Typography,
  Collapse,
} from "@mui/material";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import LogoIcon from "../../vendor/_new/image/logo.svg";
import { ReactComponent as DropdownIcon } from "../../vendor/_new/icon/arrow_down.svg";
import { ReactComponent as DropupIcon } from "../../vendor/_new/icon/arrow_up.svg";
import { ReactComponent as LogoutIcon } from "../../vendor/_new/icon/navbar/account/logout.svg";
import { ReactComponent as AccountmanagementIcon } from "../../vendor/_new/icon/navbar/account/profile.svg";

import { ReactComponent as OrganisationIcon } from "../../vendor/_new/icon/sidebar/organisation.svg";
import { ReactComponent as UsersIcon } from "../../vendor/_new/icon/sidebar/users.svg";
import { ReactComponent as EnginesIcon } from "../../vendor/_new/icon/sidebar/engines.svg";
import { ReactComponent as RegionIcon } from "../../vendor/_new/icon/sidebar/region.svg";
import { ReactComponent as RulesIcon } from "../../vendor/_new/icon/sidebar/rules.svg";
import { ReactComponent as SSLIcon } from "../../vendor/_new/icon/sidebar/ssl.svg";
import { ReactComponent as CaptchaIcon } from "../../vendor/_new/icon/sidebar/captcha.svg";
import { ReactComponent as ApiKeysIcon } from "../../vendor/_new/icon/sidebar/api_keys.svg";
import StorageIcon from "@mui/icons-material/Storage";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { ReactComponent as SettingsIcon } from "../../vendor/_new/icon/sidebar/settings.svg";
import { ReactComponent as BotIcon } from "../../vendor/_new/icon/sidebar/bot.svg";
import { ReactComponent as DdosIcon } from "../../vendor/_new/icon/sidebar/ddos.svg";
import { ReactComponent as LogsIcon } from "../../vendor/_new/icon/sidebar/logs.svg";
import { ReactComponent as ProfileIcon } from "../../vendor/_new/icon/sidebar/profile.svg";
// Alerts sidebar uses the same vendor bell icon as the topbar bell to keep
// the icon vocabulary consistent across the super-admin surface.
import { ReactComponent as AlertsIcon } from "../../vendor/_new/icon/navbar/notification.svg";

import NotificationBell from "../../components/pages/_new/super/NotificationBell";

import colors from "../../utils/colors";
import "../../vendor/perfect-scrollbar.css";

import useAuth from "../../hooks/useAuth";
import useUser from "../../hooks/super/useUser";

const SuperAdminItems = [
  {
    title: "Organisation",
    path: "/super/application/organisation",
    icon: OrganisationIcon,
    subItems: [],
  },
  {
    title: "Users",
    path: "/super/application/user",
    icon: UsersIcon,
    subItems: [
      { title: "Administrators", path: "/super/application/admins/list" },
      { title: "User List", path: "/super/application/user/list" },
      { title: "User Report", path: "/super/application/user/report" },
    ],
  },
  // Engines section (Pingora / BM / AD) on the redesigned monitoring pages.
  // Step 7a (2026-05-30) deleted the legacy "WAF Engines" group; the
  // Engines Configuration entry (edge cert mgmt) is preserved here.
  {
    title: "Engines",
    path: "/super/engines",
    icon: EnginesIcon,
    subItems: [
      { title: "Pingora", path: "/super/engines/pingora" },
      { title: "BM-Engine", path: "/super/engines/bm" },
      { title: "AD-Engine", path: "/super/engines/ad" },
      { title: "Engines Configuration", path: "/super/application/waf_edge/config" },
    ],
  },
  // System-scope alert console — surfaces ENGINE_HEALTH / LICENSE_BREACH
  // / QUOTA_REQUEST etc. (notifications with organisationId IS NULL).
  // Sits between Engines and Licensing: operationally, you check alerts
  // BEFORE drilling into the engine that fired them.
  {
    title: "Alerts",
    path: "/super/alerts",
    icon: AlertsIcon,
    subItems: [],
  },
  // Phase 1c — super-admin licensing v2. Sits next to Engines because both
  // are operational super-admin surfaces (one watches health, the other
  // grants entitlements). Tenant paywall is untouched here.
  {
    title: "Licensing",
    path: "/super/licensing",
    icon: WorkspacePremiumIcon,
    subItems: [],
  },
  {
    title: "Regions",
    path: "/super/application/region/list",
    icon: RegionIcon,
    subItems: [],
  },
  {
    title: "Rules",
    path: "/super/application/rule",
    icon: RulesIcon,
    subItems: [
      { title: "Layerd AI Rules", path: "/super/application/rule/sd" },
      { title: "Custom Rules", path: "/super/application/rule/custom" },
      { title: "Threat Insights", path: "/super/threat-insights" },
    ],
  },
  {
    title: "SSL",
    path: "/super/application/ssl",
    icon: SSLIcon,
    subItems: [],
  },
  {
    title: "Captcha",
    path: "/super/application/captcha",
    icon: CaptchaIcon,
    subItems: [
      { title: "General Settings", path: "/super/application/captcha/general" },
      { title: "API Keys", path: "/super/application/captcha/api_keys" },
      { title: "Block Pages", path: "/super/application/captcha/block_pages" },
    ],
  },
  {
    title: "API Keys",
    path: "/super/application/apikey",
    icon: ApiKeysIcon,
    subItems: [
      { title: "OTX", path: "/super/application/apikey/otx" },
      { title: "AbuseIPDB", path: "/super/application/apikey/abuseipdb" },
    ],
  },
  {
    title: "ClickHouse",
    path: "/super/application/clickhouse",
    icon: StorageIcon,
  },
  {
    title: "General",
    path: "/super/application/general",
    icon: SettingsIcon,
    subItems: [
      { title: "Email", path: "/super/application/general/email" },
      { title: "Invoice", path: "/super/application/general/invoice" },
      { title: "Notification", path: "/super/application/general/notification" },
    ],
  },
  {
    title: "Bot Mgmt",
    path: "/super/application/bm",
    icon: BotIcon,
    subItems: [
      { title: "AWS S3 Configuration", path: "/super/application/bm/aws_s3" },
    ],
  },
  {
    title: "SmartParse WAF",
    path: "/super/application/smartparse",
    icon: RulesIcon,
    subItems: [],
  },
  {
    title: "Anti DDoS",
    path: "/super/application/ad",
    icon: DdosIcon,
    subItems: [
      { title: "Anti DDoS Configuration", path: "/super/application/ad/config" },
      { title: "Anti DDoS Exception", path: "/super/application/ad/exception" },
      { title: "Active Blocks (Redis)", path: "/super/application/ad/blocks" },
    ],
  },
  {
    title: "System Config",
    path: "/super/application/global-config",
    icon: SettingsIcon,
    subItems: [],
  },
  {
    title: "Audit Log",
    path: "/super/application/log",
    icon: LogsIcon,
    subItems: [],
  },
  // Bug reports (tenant submissions) + the JIRA integration that
  // ships them to engineering. Grouped under "Support" because both
  // sit at the customer-facing seam between in-product feedback and
  // engineering triage.
  {
    title: "Support",
    path: "/super/bug-reports",
    icon: LogsIcon,
    subItems: [
      { title: "Bug Reports", path: "/super/bug-reports" },
      { title: "JIRA Integration", path: "/super/integrations/jira" },
    ],
  },
  {
    title: "Profile",
    path: "/super/application/profile",
    icon: ProfileIcon,
    subItems: [],
  },
];

export const generateMenuSx = (width, height = 32) => {
  return {
    width,
    height,
    p: 2,
    cursor: "pointer",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: colors.primary["alpha"],
      "& .MuiTypography-root": {
        color: colors.primary[300],
      },
      "& svg": {
        color: colors.primary[300],
      },
    },
    "& svg": {
      color: colors.neutral[600],
    },
  };
};

export const stringAvatar = (name) => {
  if (!name || name.split(" ").length < 2) {
    return { children: name ? name[0] : "A" };
  }
  return {
    children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
  };
};

const SuperAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const router = useLocation();
  const currentRoute = router.pathname;

  const { user, signOut } = useAuth();
  const { getOrganisations } = useUser();

  const [expandedMenus, setExpandedMenus] = useState([]);

  useEffect(() => {
    getOrganisations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMenu = (title) => {
    setExpandedMenus((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const isMenuActive = (item) => {
    if (currentRoute === item.path) return true;
    if (item.subItems?.some((sub) => currentRoute === sub.path || currentRoute.startsWith(sub.path + "/"))) return true;
    return currentRoute.startsWith(item.path + "/") || currentRoute === item.path;
  };

  const isSubItemActive = (subItem) => {
    return currentRoute === subItem.path || currentRoute.startsWith(subItem.path + "/");
  };

  useEffect(() => {
    SuperAdminItems.forEach((item) => {
      if (item.subItems?.some((sub) => currentRoute.includes(sub.path))) {
        if (!expandedMenus.includes(item.title)) {
          setExpandedMenus((prev) => [...prev, item.title]);
        }
      }
    });
  }, [currentRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const accountMenuOpen = Boolean(accountAnchorEl);
  const handleAccountMenuOpen = (event) => setAccountAnchorEl(event.currentTarget);
  const handleAccountMenuClose = () => setAccountAnchorEl(null);

  return (
    <Box sx={{ width: "100vw", height: "100vh", backgroundColor: colors.neutral[50], overflow: "hidden" }}>
      <Stack direction="row" sx={{ height: "100%" }}>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: 260,
            height: "100vh",
            backgroundColor: "#FFFFFF",
            borderRight: `1px solid ${colors.neutral[200]}`,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              px: 4,
              py: 3,
              borderBottom: `1px solid ${colors.neutral[200]}`,
            }}
          >
            <img src={LogoIcon} alt="Logo" style={{ width: 32, height: "auto" }} />
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: colors.neutral[900],
              }}
            >
              Super Admin
            </Typography>
          </Stack>

          <ReactPerfectScrollbar style={{ flex: 1, padding: "16px 12px" }}>
            <Stack spacing={0.5}>
              {SuperAdminItems.map((item, idx) => {
                const Icon = item.icon;
                const isActive = isMenuActive(item);
                const isExpanded = expandedMenus.includes(item.title);
                const hasSubItems = item.subItems && item.subItems.length > 0;

                return (
                  <Box key={`menu-${idx}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={() => {
                        if (hasSubItems) {
                          toggleMenu(item.title);
                        } else {
                          navigate(item.path);
                        }
                      }}
                      sx={{
                        px: 3,
                        py: 2,
                        cursor: "pointer",
                        borderRadius: "8px",
                        backgroundColor: isActive ? colors.primary.alpha : "transparent",
                        "&:hover": {
                          backgroundColor: colors.primary.alpha,
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Icon
                          style={{
                            width: 20,
                            height: 20,
                            color: isActive ? colors.primary[300] : colors.neutral[600],
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 14,
                            lineHeight: "21px",
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? colors.primary[300] : colors.neutral[700],
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Stack>
                      {hasSubItems && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {isExpanded ? (
                            <DropupIcon style={{ width: 16, height: 16, color: colors.neutral[500] }} />
                          ) : (
                            <DropdownIcon style={{ width: 16, height: 16, color: colors.neutral[500] }} />
                          )}
                        </Box>
                      )}
                    </Stack>
                    {hasSubItems && (
                      <Collapse in={isExpanded}>
                        <Stack spacing={0.25} sx={{ pl: 7, py: 1 }}>
                          {item.subItems.map((subItem, subIdx) => {
                            const isSubActive = isSubItemActive(subItem);
                            return (
                              <Typography
                                key={`submenu-${subIdx}`}
                                onClick={() => navigate(subItem.path)}
                                sx={{
                                  fontSize: 13,
                                  lineHeight: "20px",
                                  color: isSubActive ? colors.primary[300] : colors.neutral[600],
                                  fontWeight: isSubActive ? 500 : 400,
                                  py: 1.5,
                                  px: 2,
                                  cursor: "pointer",
                                  borderRadius: "6px",
                                  "&:hover": {
                                    backgroundColor: colors.primary.alpha,
                                    color: colors.primary[300],
                                  },
                                }}
                              >
                                {subItem.title}
                              </Typography>
                            );
                          })}
                        </Stack>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </ReactPerfectScrollbar>
        </Box>

        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 260,
            right: 0,
            height: 64,
            backgroundColor: "#FFFFFF",
            borderBottom: `1px solid ${colors.neutral[200]}`,
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 6,
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              color: colors.neutral[900],
            }}
          >
            Super Admin Dashboard
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2}>
          <NotificationBell />
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            onClick={handleAccountMenuOpen}
            sx={{ cursor: "pointer" }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: colors.primary[300],
                fontSize: 14,
              }}
              {...stringAvatar(user?.username || user?.email || "Admin User")}
            />
            <Stack spacing={0}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: colors.neutral[900],
                }}
              >
                {user?.username || user?.email || "Admin"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: colors.neutral[500],
                }}
              >
                Super Admin
              </Typography>
            </Stack>
            <DropdownIcon style={{ width: 16, height: 16, color: colors.neutral[500] }} />
          </Stack>
          </Stack>

          <Menu
            anchorEl={accountAnchorEl}
            open={accountMenuOpen}
            onClose={handleAccountMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: "8px",
                minWidth: 200,
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                mt: 1,
              },
            }}
          >
            <Stack sx={{ p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => {
                  handleAccountMenuClose();
                  navigate("/super/application/profile");
                }}
                sx={generateMenuSx("100%")}
              >
                <AccountmanagementIcon style={{ width: 20, height: 20 }} />
                <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                  Profile
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => {
                  signOut();
                  handleAccountMenuClose();
                }}
                sx={generateMenuSx("100%")}
              >
                <LogoutIcon style={{ width: 20, height: 20 }} />
                <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
                  Logout
                </Typography>
              </Stack>
            </Stack>
          </Menu>
        </Box>

        <Box
          sx={{
            marginLeft: "260px",
            marginTop: "64px",
            width: "calc(100% - 260px)",
            height: "calc(100vh - 64px)",
            overflow: "auto",
            backgroundColor: colors.neutral[50],
          }}
        >
          <Box sx={{ p: 6 }}>
            {children}
            <Outlet />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default SuperAdminLayout;
